import {
  AssetTypeSchema,
  languageOf,
  type AppResource,
  type AssetType,
  type InventoryCell,
  type InventoryListing,
  type ListingSnapshotResource,
} from '@studio/shared';
import { useId, useRef, useState, type ReactNode } from 'react';
import { assetTypeLabel } from '../app/history.js';
import { api, ApiError } from '../shell/api.js';
import { Failure } from '../shell/EmptyState.js';
import { useNavigate } from '../shell/router.js';
import { useAsync } from '../shell/useAsync.js';
import { controlGraphicsOf, controlOf, countryName, frozenTrailerOf, languageName, targetOf } from './model.js';

/**
 * Why the transition cannot happen, in the words the operator reads.
 *
 * Every reason is named: an empty place would leave the operator guessing whether the
 * button is broken or the storefront has nothing to compare with.
 *
 * The archive is not judged here. The Inventory never lists an archived App with its
 * cells; an App archived after this page was read is refused by the Studio, and its
 * words reach the operator the way every other refusal does.
 *
 * A missing Control is no reason either: a Control "may be absent without blocking the
 * Experiment", and the Studio creates one without it. That is said beside the button, by
 * `noReading` or `controlFrom`.
 */
function refusalOf(cell: InventoryCell): string | null {
  if (cell.snapshotId === null) {
    return 'Это чтение ещё не прочитано: сначала просканируйте этот Store Listing.';
  }
  return null;
}

/**
 * That the Experiment starts without a Control because no reading is one for this
 * Target, said about the Target and the languages one was looked for in.
 */
function noReading(listing: InventoryListing, cell: InventoryCell): string {
  const own = languageName(languageOf(cell.locale));
  const standard = listing.defaultLocale === null ? null : languageName(languageOf(listing.defaultLocale));
  const missing =
    standard === null
      ? `на языке Target (${own}), а язык по умолчанию ещё не известен`
      : standard === own
        ? `на языке Target (${own}), он же язык по умолчанию`
        : `ни на языке Target (${own}), ни на языке по умолчанию (${standard})`;
  return `Experiment начнётся без Control: для Target ${countryName(cell.market)} · ${languageName(cell.locale)} у этого Store Listing нет чтения без Translation Gap ${missing}.`;
}

/** What a reading lacks when it shows nothing of an Asset Type, and when it shows that without a file. */
const LACKING: Record<Exclude<AssetType, 'previewVideo'>, { readonly absent: string; readonly unfetched: string }> = {
  appIcon: { absent: 'нет иконки', unfetched: 'нет файла иконки: Studio не смог его получить' },
  featureGraphic: { absent: 'нет Feature graphic', unfetched: 'нет файла Feature graphic: Studio не смог его получить' },
  screenshotSet: { absent: 'нет скриншотов', unfetched: 'нет файлов скриншотов: Studio не смог их получить' },
};

/**
 * A reading as it came back - or why it did not - always under the id it was asked for:
 * a failure of one reading is never the answer for another.
 */
export type ReadingById =
  | { readonly id: string; readonly snapshot: ListingSnapshotResource; readonly error: null }
  | { readonly id: string; readonly snapshot: null; readonly error: string };

export async function readById(id: string): Promise<ReadingById> {
  try {
    return { id, snapshot: await api.readSnapshot(id), error: null };
  } catch (caught: unknown) {
    return { id, snapshot: null, error: caught instanceof ApiError ? caught.message : 'Не удалось связаться со Studio.' };
  }
}

/**
 * What the Experiment will be compared with, once the reading the Control comes from is
 * read: that reading, named by its Market Locale and its date, or why there is none.
 *
 * The Studio makes the Control of the graphics of the chosen type it holds a file of, and
 * the screen asks the same of the same reading - so what it says is what the Experiment
 * gets, not a guess about it.
 *
 * A trailer is the one graphic without a file. Until `Mark Live` there is nothing to
 * preview, and `Mark Live` (`freezeControl`) freezes the YouTube address of the reading's
 * first trailer - so the screen promises that address only when that trailer has one.
 */
function controlFrom(control: InventoryCell, snapshot: ListingSnapshotResource, assetType: AssetType): string {
  const where = `${countryName(control.market)} · ${languageName(control.locale)}`;
  const graphics = controlGraphicsOf(snapshot, assetType);
  if (graphics.kept.length > 0) {
    const when = new Date(snapshot.capturedAt).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' });
    return `Control — чтение ${where} от ${when}.`;
  }
  if (assetType === 'previewVideo') {
    if (graphics.shown.length === 0) return `Experiment начнётся без Control: в чтении ${where} нет трейлера.`;
    return frozenTrailerOf(snapshot) === null
      ? `Experiment начнётся без Control: в чтении ${where} у трейлера нет адреса на YouTube.`
      : `Превью Control не будет: трейлер не хранится файлом. При Mark Live Control зафиксирует адрес трейлера на YouTube из чтения ${where}.`;
  }
  const lacking = graphics.shown.length === 0 ? LACKING[assetType].absent : LACKING[assetType].unfetched;
  return `Experiment начнётся без Control: в чтении ${where} ${lacking}.`;
}

/**
 * The Inventory the operator came from, with the search, the filter and the open cell.
 *
 * The filters live in the address, and so does the open cell: coming back has to land
 * on the same reading, not on the whole list again.
 */
function backAddress(): string {
  return `/inventory${window.location.search}`;
}

/** The Brief the transition starts from, said in the words of the cell it came from. */
function carriedBrief(label: string, cell: InventoryCell): string {
  return `Креатив для ${countryName(cell.market)} (${languageName(cell.locale)}) по Store Listing «${label}».`;
}

/**
 * The way out of the Inventory and into an Experiment.
 *
 * The App, the Store Listing and the Target are already chosen - they are the cell the
 * operator is looking at. The Asset Type is the one choice left, and it is made here,
 * before the transition, not on the other side of it.
 *
 * The transition itself costs nothing: it creates an Experiment and opens its Brief. No
 * generation is started and no provider is asked for anything.
 */
export function StartExperiment({
  app,
  listing,
  cell,
  cellReading,
  onRereadCell,
}: {
  app: AppResource;
  listing: InventoryListing;
  cell: InventoryCell;
  /** The cell's own reading as the view it sits in has read it; null while it loads. */
  cellReading: ReadingById | null;
  /** Reads the cell's own reading again, for the view and for this transition at once. */
  onRereadCell: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const group = useId();
  const aboutControl = useId();
  const [assetType, setAssetType] = useState<AssetType>('screenshotSet');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One press, one Experiment: the second press finds the first one already on its way.
  const started = useRef(false);

  const { storeListing } = listing;
  const label = storeListing.kind === 'main' ? 'Main Store Listing' : storeListing.name;
  const refusal = refusalOf(cell);
  // The reading the Studio takes the Control out of, by its own rule, and that reading
  // itself: only it tells whether it holds the graphics of the chosen type.
  const control = refusal === null ? controlOf(listing, cell) : null;
  const controlId = control?.snapshotId ?? null;
  // Most often the Control is the cell's own reading, already read by the view above:
  // it is taken from there rather than read a second time.
  const own = controlId !== null && controlId === cell.snapshotId;
  const reading = useAsync<ReadingById | null>(
    async () => (controlId === null || own ? null : readById(controlId)),
    [controlId, own],
  );
  // `useAsync` keeps the last value - and the last failure - while the next one loads:
  // another reading must never answer for this one, neither with its graphics nor with
  // its failure.
  const answered = own ? cellReading : reading.value;
  const current = controlId !== null && answered?.id === controlId ? answered : null;
  const snapshot = current?.snapshot ?? null;
  const controlUnread = current?.error ?? null;
  // Until that reading is here the screen says neither that there will be a Control nor
  // that there will not; a failed read is said below, and leaves the transition open.
  const aboutTheControl: { readonly text: string; readonly busy: boolean } | null =
    refusal !== null
      ? null
      : control === null
        ? { text: noReading(listing, cell), busy: false }
        : snapshot !== null
          ? { text: controlFrom(control, snapshot, assetType), busy: false }
          : controlUnread === null
            ? { text: 'Проверяю Control для этого типа креатива…', busy: true }
            : null;

  const start = (): void => {
    if (started.current || refusal !== null) return;
    started.current = true;
    setBusy(true);
    setError(null);
    void api
      .createExperiment({
        appId: app.id,
        assetType,
        // The Main Store Listing is the Experiment's `null`: the same thing the Brief says.
        storeListingId: storeListing.kind === 'main' ? null : storeListing.id,
        target: targetOf(cell),
        brief: carriedBrief(label, cell),
      })
      .then((experiment) => {
        navigate(`/experiments/${experiment.id}/brief?back=${encodeURIComponent(backAddress())}`);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : 'Не удалось начать Experiment.');
        setBusy(false);
        started.current = false;
      });
  };

  return (
    <section className="start-experiment" aria-label="Experiment из этого чтения">
      <p className="eyebrow">Experiment из этого чтения</p>
      <p className="note">
        {`Перенесётся: App ${app.displayName} · Store Listing «${label}» · Target ${countryName(cell.market)} · ${languageName(cell.locale)}`}
      </p>
      <fieldset className="asset-choices">
        <legend>Тип креатива</legend>
        {AssetTypeSchema.options.map((option) => (
          <label key={option} className={option === assetType ? 'asset-choice active' : 'asset-choice'}>
            <input
              type="radio"
              name={`asset-type-${group}`}
              value={option}
              checked={option === assetType}
              onChange={() => setAssetType(option)}
            />
            {assetTypeLabel(option)}
          </label>
        ))}
      </fieldset>
      <button
        type="button"
        className="primary"
        disabled={busy || refusal !== null}
        aria-describedby={aboutTheControl === null ? undefined : aboutControl}
        onClick={start}
      >
        {busy ? 'Начинаю…' : 'Начать Experiment'}
      </button>
      {/* A missing Control is a warning, not an error: the transition is open, and nothing has failed. */}
      {aboutTheControl === null ? null : (
        <p className="note" id={aboutControl} aria-busy={aboutTheControl.busy ? 'true' : undefined}>
          {aboutTheControl.text}
        </p>
      )}
      {/* A reading that could not be read leaves the question open, and the transition too. */}
      {controlUnread === null ? null : (
        <Failure
          message={`Не удалось проверить Control: ${controlUnread} Experiment всё равно можно начать — Studio возьмёт Control сам, если он есть.`}
          onRetry={own ? onRereadCell : reading.reload}
        />
      )}
      {refusal === null ? null : (
        <p className="failure" role="alert">
          {refusal}
        </p>
      )}
      {error === null ? null : (
        <p className="failure" role="alert">
          {error}
        </p>
      )}
      <p className="note">Переход открывает Brief: он ничего не генерирует и к провайдеру не ходит.</p>
    </section>
  );
}
